<script setup lang="ts">
import {BackingInterviewStep} from "@/backing/interfaces/backing";
import Icon from "@/core/components/icon/Icon.vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {computed, ref} from "vue";
import InterviewInstanceService from "@/interview/services/InterviewInstanceService";
import {DropdownMenu, DropdownMenuContent, DropdownMenuTrigger} from "@/core/components/ui/dropdown-menu";
import {Button} from "@/core/components/ui/button";
import dateHelper from "@/core/helpers/dateHelper";
import {RotateCcw} from "@lucide/vue";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/core/components/ui/tooltip";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from "@/core/components/ui/alert-dialog";
import PatientInterviewLinks from "@/backing/components/interviews/PatientInterviewLinks.vue";
import Customer from "@/customer/interfaces/Customer";
import {InterviewInstance} from "@/interview/interfaces/interview";

type StepInstance = Omit<InterviewInstance, "interview">;

const emits = defineEmits<{
    (e: 'viewInstance', instanceId: number): void;
    (e: 'startInstance'): void;
    (e: 'restartInstance'): void;
}>();

const {step, backingId = null, customer = null} = defineProps<{
    /** The step */
    step: BackingInterviewStep;
    /** The backing and the patient a link is handed for; the links are not offered without them. */
    backingId?: number | null;
    /** Null for a beneficiary, who holds no link of their own. */
    customer?: Customer | null;
}>();

/**
 * The patient fills this interview themselves, on the mobile app or through
 * the link the pharmacy hands them. Only the auto-evaluations, for now.
 */
const filledByPatient = computed(() => step.auto_evaluation);

/** Completed by the patient or at the counter, or finished by the pharmacist. */
const isDone = (instance: StepInstance): boolean => filledByPatient.value
    ? InterviewInstanceService.isCompleted(instance)
    : InterviewInstanceService.isInterviewFinished(instance);

const doneAt = (instance: StepInstance): string => (filledByPatient.value ? instance.completed_at : instance.ended_at) as string;

// Latest first, ties broken as the API resumes them.
const sortedInstances = computed(() => step.interview_instances
    ? [...step.interview_instances].sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime() || b.id - a.id)
    : null);

/**
 * Get the latest interview instance for the step: for an interview the
 * patient fills, the one their app and links resume.
 */
const latestInstance = computed(() => sortedInstances.value?.[0] ?? null);

const latestInstanceFinished = computed(() => latestInstance.value ? isDone(latestInstance.value) : false);

/** Not sent yet: the patient may still fill it, or the pharmacist fill a new one with them. */
const awaitingPatient = computed(() => filledByPatient.value && !latestInstanceFinished.value);

/**
 * Compute the action button properties depending on the interview instance state.
 */
const action = computed(() => {
    // An auto-evaluation is filled at the counter as any interview: a new instance, never the patient's draft.
    if (!latestInstance.value || awaitingPatient.value) {
        return {
            label: "Commencer",
            primary: true,
            appendIcon: "mdi-chevron-right",
            click: () => emits('startInstance')
        };
    }

    if (!latestInstanceFinished.value) {
        return {
            label: "Finaliser le compte-rendu",
            primary: true,
            appendIcon: "mdi-chevron-right",
            click: () => emits('viewInstance', latestInstance.value!.id)
        };
    }

    return {
        label: filledByPatient.value ? "Voir les réponses" : "Voir le compte-rendu",
        primary: false,
        prependIcon: "mdi-file-document-edit",
        click: () => emits('viewInstance', latestInstance.value!.id)
    };
});

/** Only once sent: until then the patient's links and « Commencer » cover it. */
const canRestart = computed(() => !!latestInstance.value && (!filledByPatient.value || latestInstanceFinished.value));

const restartDialogOpen = ref(false);

/** Starting over an auto-evaluation changes what the patient sees: it is confirmed first. */
const restart = () => {
    if (filledByPatient.value) {
        restartDialogOpen.value = true;
    } else {
        emits('startInstance');
    }
};

/** The former instances; for an interview the patient fills, only the ones sent, a draft left behind telling nothing. */
const history = computed(() => sortedInstances.value
    ?.filter(instance => instance.id !== latestInstance.value?.id)
    .filter(instance => !filledByPatient.value || isDone(instance)) ?? []);
</script>

<template>
  <div class="py-3 px-4 flex flex-col gap-3">
    <div class="flex flex-row items-center h-min gap-4 justify-between">
      <div>
        <div class="min-w-28">
          <icon :size="20" name="mdi-chat-outline" color="primary" class="mr-2" />
          <span class="">{{ step.name }}</span>
          <icon
            v-if="latestInstance"
            :size="latestInstanceFinished ? 20 : 17"
            :name="latestInstanceFinished ? 'mdi-check-bold' : 'mdi-check-outline'"
            class="ml-1"
            color="secondary"
          />
          <div v-if="latestInstance" class="ml-7">
            <p v-if="filledByPatient && latestInstanceFinished" class="text-sm text-gray-600">
              {{ InterviewInstanceService.isCompletedByPatient(latestInstance) ? 'Envoyé par le patient' : 'Rempli au comptoir' }}
              le {{ dateHelper.shortFormat(doneAt(latestInstance)) }}
            </p>
            <p v-else-if="awaitingPatient" class="text-sm text-gray-600">
              En attente du patient depuis le {{ dateHelper.shortFormat(latestInstance.started_at) }}
            </p>
            <p v-else class="text-sm text-gray-600">
              Entretien démarré le {{ dateHelper.shortFormat(latestInstance.started_at) }}
            </p>
          </div>
        </div>
      </div>
      <div class="flex flex-col align-end">
        <div class="flex gap-1">
          <BaseButton
            density="comfortable"
            :primary="action.primary"
            :append-icon="action.appendIcon"
            :prepend-icon="action.prependIcon"
            @click="action.click"
          >
            {{ action.label }}
          </BaseButton>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Button v-if="canRestart" variant="ghost" size="icon" @click="restart">
                  <RotateCcw class="text-primary" />
                </Button>
              </TooltipTrigger>
              <TooltipContent class="bg-primary font-medium">
                {{ filledByPatient ? "Recommencer l'auto-évaluation" : 'Redémarrer un entretien' }}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <DropdownMenu v-if="history.length">
          <DropdownMenuTrigger as-child @click="$event.stopPropagation()">
            <Button variant="link" class="text-primary">
              historique
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <div class="max-h-60 overflow-y-auto">
              <div
                v-for="instance in history"
                :key="instance.id"
                class="p-2 hover:bg-gray-100 cursor-pointer text-sm"
                @click="emits('viewInstance', instance.id)"
              >
                <p>
                  Entretien démarré le {{ dateHelper.mediumFormat(instance.started_at) }}
                </p>
                <p v-if="isDone(instance)" class="text-xs text-gray-600">
                  Terminé le {{ dateHelper.mediumFormat(doneAt(instance)) }}
                </p>
                <p v-else class="text-xs text-gray-600">
                  À finaliser
                </p>
              </div>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
    <PatientInterviewLinks
      v-if="awaitingPatient && customer && backingId"
      :customer="customer"
      :backing-id="backingId"
      :interview-id="step.resource_id"
    />
    <AlertDialog v-model:open="restartDialogOpen">
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Recommencer l'auto-évaluation ?</AlertDialogTitle>
          <AlertDialogDescription>
            <template v-if="latestInstance">
              Les réponses du {{ dateHelper.shortFormat(doneAt(latestInstance)) }} restent consultables dans l'historique.
            </template>
            Le patient devra remplir à nouveau le questionnaire : envoyez-lui un lien, ou remplissez-le avec lui.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Annuler</AlertDialogCancel>
          <AlertDialogAction class="bg-primary" @click="emits('restartInstance')">
            Recommencer
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
</template>
